import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import AdminShell from "./AdminShell";
import { firestore, storage } from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

/* ---------- UI (nya admin-stilen) ---------- */

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: 1.1fr 0.9fr;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 14px;
  background: #fff;
`;

const H3 = styled.h3`
  margin: 0;
  color: #111827;
`;

const Label = styled.div`
  font-weight: 900;
  margin: 10px 0 6px;
  color: #111827;
`;

const Input = styled.input`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  font-weight: 800;

  &:focus {
    outline: none;
    border-color: rgba(15, 118, 110, 0.45);
    box-shadow: 0 0 0 4px rgba(15, 118, 110, 0.12);
  }
`;

const Select = styled.select`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  font-weight: 800;
  background: #fff;

  &:focus {
    outline: none;
    border-color: rgba(15, 118, 110, 0.45);
    box-shadow: 0 0 0 4px rgba(15, 118, 110, 0.12);
  }
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 12px;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid rgba(15, 118, 110, 0.22);
  background: #0f766e;
  color: #fff;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: wait;
    transform: none;
  }

  &:hover {
    filter: brightness(1.03);
    transform: translateY(-1px);
  }
`;

const Ghost = styled(Btn)`
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;

  &:hover {
    background: #f9fafb;
  }
`;

const Danger = styled(Btn)`
  background: rgba(233, 78, 27, 0.08);
  color: #e94e1b;
  border: 1px solid rgba(233, 78, 27, 0.28);
`;

const Muted = styled.div`
  color: #6b7280;
  font-weight: 800;
  font-size: 12px;
  line-height: 1.45;
`;

const Item = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 12px;
  display: grid;
  gap: 10px;
`;

const ItemTop = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
`;

const Thumb = styled.img`
  width: 84px;
  height: 64px;
  object-fit: cover;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #000;
`;

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 950;
  color: #0f766e;
  background: rgba(15, 118, 110, 0.1);
  border: 1px solid rgba(15, 118, 110, 0.18);
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 12px;
`;

const LinkList = styled.div`
  display: grid;
  gap: 8px;
  margin-top: 10px;
`;

const LinkRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const MiniBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(233, 78, 27, 0.28);
  background: rgba(233, 78, 27, 0.08);
  color: #e94e1b;
  font-weight: 950;
  border-radius: 999px;
  padding: 8px 12px;
  cursor: pointer;

  &:hover {
    filter: brightness(1.05);
  }
`;

/* ---------- Helpers ---------- */

const TYPES = [
  "Klubbmästerskap",
  "Region",
  "Liga",
  "Veckotävling",
  "Lokal tävling",
  "Annat",
];

function makeStoragePath(file) {
  const safeName = (file?.name || "competition")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9.\-_]/g, "");
  return `competitions/${Date.now()}-${safeName}`;
}

function normalizeLinks(arr) {
  return (arr || [])
    .map((s) => (s || "").trim())
    .filter(Boolean);
}

/* ---------- Component ---------- */

export default function AdminCompetitions() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  // form
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [customType, setCustomType] = useState("");
  const [links, setLinks] = useState([""]);
  const [file, setFile] = useState(null);

  // upload status
  const [uploading, setUploading] = useState(false);
  const [imageURL, setImageURL] = useState("");
  const [storagePath, setStoragePath] = useState("");

  const previewUrl = useMemo(() => {
    if (file) return URL.createObjectURL(file);
    if (imageURL) return imageURL;
    return "";
  }, [file, imageURL]);

  useEffect(() => {
    return () => {
      if (file && previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const load = async () => {
    const snap = await getDocs(collection(firestore, "competitions"));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // sort newest first if timestamp exists
    list.sort((a, b) => {
      const at = a.createdAt?.toMillis?.() || 0;
      const bt = b.createdAt?.toMillis?.() || 0;
      if (bt !== at) return bt - at;
      return String(b.id).localeCompare(String(a.id));
    });

    setItems(list);
  };

  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setEditingId(null);
    setName("");
    setType("");
    setCustomType("");
    setLinks([""]);
    setFile(null);
    setImageURL("");
    setStoragePath("");
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setName(it.name || "");
    setType(TYPES.includes(it.type) ? it.type : "Annat");
    setCustomType(TYPES.includes(it.type) ? "" : it.type || "");
    setLinks((it.links && it.links.length ? it.links : [""]).map(String));
    setImageURL(it.image || "");
    setStoragePath(it.storagePath || "");
    setFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const uploadImageIfNeeded = async () => {
    if (!file) return { image: imageURL || null, storagePath: storagePath || null };

    setUploading(true);
    try {
      const path = makeStoragePath(file);
      const r = ref(storage, path);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);
      return { image: url, storagePath: path };
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!name.trim()) return alert("Skriv ett tävlingsnamn.");
    if (!type) return alert("Välj tävlingstyp.");

    setBusy(true);
    try {
      const uploaded = await uploadImageIfNeeded();

      const finalType = type === "Annat" ? (customType || "").trim() : type;
      if (!finalType) {
        setBusy(false);
        return alert("Skriv vilken typ det är (Annat).");
      }

      const payload = {
        name: name.trim(),
        type: finalType,
        links: normalizeLinks(links),
        image: uploaded.image || null, // behåller "image" som du redan har i db
        storagePath: uploaded.storagePath || null,
        updatedAt: serverTimestamp(),
      };

      if (editingId) {
        await updateDoc(doc(firestore, "competitions", editingId), payload);
      } else {
        await addDoc(collection(firestore, "competitions"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }

      alert("Sparat!");
      reset();
      await load();
    } catch (e) {
      alert(e?.message || "Kunde inte spara tävling.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm("Ta bort tävling?")) return;

    setBusy(true);
    try {
      await deleteDoc(doc(firestore, "competitions", it.id));

      // (valfritt) radera bildfil om vi har storagePath
      if (it.storagePath) {
        try {
          await deleteObject(ref(storage, it.storagePath));
        } catch {
          // ignore
        }
      }

      await load();
    } catch (e) {
      alert(e?.message || "Kunde inte ta bort tävling.");
    } finally {
      setBusy(false);
    }
  };

  const setLink = (idx, value) => {
    setLinks((prev) => prev.map((x, i) => (i === idx ? value : x)));
  };

  const addLink = () => setLinks((prev) => [...prev, ""]);
  const removeLink = (idx) =>
    setLinks((prev) => prev.filter((_, i) => i !== idx));

  return (
    <AdminShell title="Tävlingar">
      <Grid>
        {/* FORM */}
        <Card>
          <H3>{editingId ? "Redigera tävling" : "Lägg in tävling"}</H3>

          <Label>Tävlingsnamn</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="T.ex. Hofors Open" />

          <Label>Tävlingstyp</Label>
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">Välj typ…</option>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>

          {type === "Annat" && (
            <>
              <Label>Annan typ</Label>
              <Input
                value={customType}
                onChange={(e) => setCustomType(e.target.value)}
                placeholder="Skriv typ…"
              />
            </>
          )}

          <Label>Länkar</Label>
          <LinkList>
            {links.map((l, idx) => (
              <LinkRow key={idx}>
                <Input
                  type="url"
                  value={l}
                  onChange={(e) => setLink(idx, e.target.value)}
                  placeholder="https://..."
                />
                {links.length > 1 && (
                  <MiniBtn type="button" onClick={() => removeLink(idx)} disabled={busy}>
                    Ta bort
                  </MiniBtn>
                )}
              </LinkRow>
            ))}
          </LinkList>

          <Row>
            <Ghost type="button" onClick={addLink} disabled={busy}>
              + Lägg till länk
            </Ghost>
          </Row>

          <Label>Bild (valfritt)</Label>
          <Input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />

          {previewUrl ? (
            <div style={{ marginTop: 10 }}>
              <Thumb
                src={previewUrl}
                alt="Preview"
                style={{ width: 220, height: 140 }}
              />
            </div>
          ) : (
            <Muted style={{ marginTop: 8 }}>
              Ingen bild vald (du kan spara ändå).
            </Muted>
          )}

          <Row>
            <Btn type="button" onClick={save} disabled={busy || uploading}>
              {uploading ? "Laddar upp…" : busy ? "Sparar…" : editingId ? "Spara" : "Skapa"}
            </Btn>
            <Ghost type="button" onClick={reset} disabled={busy || uploading}>
              Rensa
            </Ghost>
          </Row>

          <Muted style={{ marginTop: 12 }}>
            Sparar i <code>competitions</code>: <b>name</b>, <b>type</b>, <b>links</b>, <b>image</b>, (extra: <b>storagePath</b>).
          </Muted>
        </Card>

        {/* LIST */}
        <Card>
          <H3>Inlagda tävlingar</H3>
          <div style={{ height: 10 }} />

          {items.length === 0 ? (
            <Muted>Inga tävlingar ännu.</Muted>
          ) : (
            items.map((it) => (
              <Item key={it.id}>
                <ItemTop>
                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontWeight: 950, color: "#111827" }}>{it.name}</div>
                    <Pill>{it.type || "Okänd typ"}</Pill>
                    {it.links?.length ? (
                      <Muted>
                        Länkar: {it.links.length}
                      </Muted>
                    ) : (
                      <Muted>Inga länkar</Muted>
                    )}
                  </div>

                  {it.image ? <Thumb src={it.image} alt="Bild" /> : null}
                </ItemTop>

                <Row>
                  <Ghost type="button" onClick={() => startEdit(it)} disabled={busy}>
                    Redigera
                  </Ghost>
                  <Danger type="button" onClick={() => remove(it)} disabled={busy}>
                    Ta bort
                  </Danger>
                </Row>
              </Item>
            ))
          )}
        </Card>
      </Grid>
    </AdminShell>
  );
}
